import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import { Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { getProviderProfiles } from "../../../../../api/admin/provider-management";
import { routes } from "../../../../../constants/routes";
import { BUSINESS_TYPE_PLANNER } from "../../../../../utils/constants";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import ProfileHeader from "../../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import ListingTable from "../../../../../common/components/listing-table/list-table";
import AdminPaginationComponent from "../../../../../common/components/admin-pagination-component/admin-pagination-component";
import { AlertComponent } from "../../../../../common/components";
import ProviderRequestSearchAndFilters from "../../provider-management/provider-requests/search-and-filters";
import ProviderProfilesFilterOptions from "../../provider-management/provider-profiles/provider-profiles-filters-options/provider-profiles-filter-options";
import {
  profileHeaders,
  plannerProfileFilterOptions,
} from "./planner-profiles-helper";
import "../../provider-management/provider-profiles/provider-profiles-list.scss";

const PAGE_SIZE = 10;

const PlannerProfilesList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>(
    location?.state?.searchText || ""
  );
  const [isResetSearch, setIsResetSearch] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<any>(
    location?.state?.activeFilters || {}
  );
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);

  const breadcrumbLinkForList = [
    { label: "Planner Management", link: routes.PLANNER_MANAGEMENT, handleClick: () => { } },
    { label: "Planner Profile", link: "#", handleClick: () => { } },
  ];

  const searchRef = useRef<{ clear: () => void }>(null);
  const isSearchTriggered = useRef<boolean>(false);

  const fetchPlannerProfiles = async (
    page: number,
    search: string,
    filters: any
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const params: any = {
        page,
        limit: PAGE_SIZE,
        offset,
        search: search,
      };
      if (filters?.businessName) {
        params.businessName = filters.businessName;
      }
      params.businessTypes =
        filters?.businessType?.length ? filters.businessType : [BUSINESS_TYPE_PLANNER];
      const response = await getProviderProfiles(params);
      if (response?.status === 200 || response?.data?.success) {
        setData(response?.data?.data?.data || []);
        setTotalRecords(response?.data?.data?.totalCount || 0);
      } else {
        setAlertType("error");
        setAlertMessage("Failed to fetch planner profiles.");
        setAlertOpen(true);
      }
    } catch (error: any) {
      setAlertType("error");
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch planner profiles."
      );
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1);
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const formattedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((item: any) => ({
      id: item.id,
      businessName: item.businessName,
      primaryContactName: item.primaryContactName,
      businessType: item.businessType,
      businessPhone: item.businessPhone,
      businessEmail: item.businessEmail,
    }));
  }, [data]);

  const appliedFilterCount = useMemo(() => {
    return Object.values(activeFilters).filter(
      (val) =>
        val !== undefined &&
        val !== null &&
        val !== "" &&
        !(Array.isArray(val) && val.length === 0)
    ).length;
  }, [activeFilters]);

  const noResultsTxt = useMemo(() => {
    if (loading) return "Loading planner profiles...";
    if (searchText && appliedFilterCount === 0)
      return "No planner profiles found matching your search criteria.";
    if (!searchText && appliedFilterCount > 0)
      return "No planner profiles found matching filter criteria.";
    if (searchText && appliedFilterCount > 0)
      return "No planner profiles found matching search and filter criteria.";
    return "No planner profiles found.";
  }, [loading, searchText, appliedFilterCount]);

  useEffect(() => {
    if (
      location.state &&
      location.state.alertMessage &&
      location?.state?.fromView
    ) {
      setAlertMessage(location.state.alertMessage);
      setAlertType(location.state.alertType || "info");
      setAlertOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state && location.state.searchText) {
      setSearchText(location.state.searchText);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (
      location.state &&
      Object.keys(location?.state?.activeFilters || {}).length > 0
    ) {
      setActiveFilters(location.state.activeFilters);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchPlannerProfiles(pageToUse, searchText || "", activeFilters);
    isSearchTriggered.current = false;
  }, [currentPage, searchText, activeFilters]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <Box className="provider-profiles-main-wrapper">
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <ProfileHeader breadcrumbLink={breadcrumbLinkForList} />
        <Box
          className="provider-profiles-filter-and-list-wrapper"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            paddingBottom: totalRecords === 0 ? "10px" : "0px",
          }}
        >
          <Box className="provider-profiles-search-and-filters">
            <ProviderRequestSearchAndFilters
              onSearch={handleSearch}
              onFilterChange={setActiveFilters}
              appliedFiltersCount={appliedFilterCount}
              isFilterOpen={isOpenFilter}
              setIsFilterOpen={setIsOpenFilter}
              searchRef={searchRef}
              localSearchText={searchText}
              isReset={isResetSearch}
              placeholder="Search by Business Name, Contact Name, etc. "
            />
          </Box>
          <Box className="provider-profiles-listing-table-wrapper">
            <ListingTable
              headers={profileHeaders}
              data={formattedData}
              onRowClick={(row) => {
                navigate(
                  `${routes.PLANNER_PROFILE_DETAILS.replace(":id", row.id)}`,
                  {
                    state: {
                      searchText: searchText,
                      activeFilters: activeFilters,
                    },
                  }
                );
              }}
              noResultsText={noResultsTxt}
            />
            <Box
              style={{ display: isOpenFilter ? "flex" : "none" }}
              className="provider-requests-filter-options filters-options-main"
            >
              <ProviderProfilesFilterOptions
                isOpenFilter={isOpenFilter}
                setIsOpenFilter={setIsOpenFilter}
                providerProfilesFilterOptions={plannerProfileFilterOptions}
                activeFilters={{
                  ...activeFilters,
                  businessName: activeFilters?.businessName,
                }}
                onApplyFilters={(filters) => {
                  setActiveFilters(filters);
                  setCurrentPage(1);
                }}
              />
            </Box>
          </Box>
        </Box>
        {totalRecords > 0 && (
          <AdminPaginationComponent
            totalRecords={totalRecords}
            pageSize={PAGE_SIZE}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </Box>
      <AlertComponent
        open={alertOpen}
        type={alertType}
        message={alertMessage || ""}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
};

export default PlannerProfilesList;
